import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const AGEGO_VERIFICATION_COOKIE = 'agego_aver='

const hasAgeGoVerification = (): boolean => {
  if (typeof document === 'undefined') {
    return false
  }

  return document.cookie.split(';').some((cookie) => cookie.trim().startsWith(AGEGO_VERIFICATION_COOKIE))
}

interface AgeGateState {
  isVerified: boolean
  isLoading: boolean
  error: string | null
  lastEventData: unknown
}

interface AgeGateActions {
  markPending: () => void
  markVerified: (eventData?: unknown) => void
  markFailed: (errorMessage?: string) => void
  syncFromCookie: () => void
  reset: () => void
}

export const useAgeGateStore = create<AgeGateState & AgeGateActions>()(
  persist(
    (set, get) => ({
      isVerified: typeof window !== 'undefined' ? hasAgeGoVerification() : false,
      isLoading: typeof window !== 'undefined' ? !hasAgeGoVerification() : true,
      error: null,
      lastEventData: null,

      markPending: () => {
        if (get().isVerified) {
          return
        }

        set({ isLoading: true, error: null })
      },

      markVerified: (eventData) => {
        set({
          isVerified: true,
          isLoading: false,
          error: null,
          lastEventData: eventData ?? null,
        })
      },

      markFailed: (errorMessage) => {
        set({
          isVerified: false,
          isLoading: false,
          error: errorMessage ?? 'La vérification d\'âge a échoué. Veuillez réessayer.',
        })
      },

      syncFromCookie: () => {
        const verified = hasAgeGoVerification()
        set({
          isVerified: verified,
          isLoading: !verified,
          error: null,
        })
      },

      reset: () => {
        const verified = hasAgeGoVerification()

        set({
          isVerified: verified,
          isLoading: !verified,
          error: null,
          lastEventData: null,
        })
      },
    }),
    {
      name: 'age-gate-storage',
      storage: {
        getItem: (name) => {
          if (typeof sessionStorage === 'undefined') {
            return null
          }

          const item = sessionStorage.getItem(name)
          return item ? JSON.parse(item) : null
        },
        setItem: (name, value) => {
          if (typeof sessionStorage === 'undefined') {
            return
          }

          sessionStorage.setItem(name, JSON.stringify(value))
        },
        removeItem: (name) => {
          if (typeof sessionStorage === 'undefined') {
            return
          }

          sessionStorage.removeItem(name)
        },
      },
      partialize: (state) => ({
        isVerified: state.isVerified,
        lastEventData: state.lastEventData,
      }),
    }
  )
)
